/**
 * Stone Investment Phone Input
 * 
 * International phone input with Stone Investment's luxury aesthetic:
 * - Country code selector
 * - Elegant styling
 * - Validation
 */

'use client';

import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export interface CountryCode {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

function getDefaultCountries(labels?: Record<string, string>): CountryCode[] {
  return [
    { code: 'FR', name: labels?.france || 'France', dialCode: '+33', flag: '🇫🇷' },
    { code: 'MU', name: labels?.mauritius || 'Mauritius', dialCode: '+230', flag: '🇲🇺' },
    { code: 'GB', name: labels?.unitedKingdom || 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
    { code: 'US', name: labels?.unitedStates || 'United States', dialCode: '+1', flag: '🇺🇸' },
    { code: 'ZA', name: labels?.southAfrica || 'South Africa', dialCode: '+27', flag: '🇿🇦' },
    { code: 'CH', name: labels?.switzerland || 'Switzerland', dialCode: '+41', flag: '🇨🇭' },
    { code: 'BE', name: labels?.belgium || 'Belgium', dialCode: '+32', flag: '🇧🇪' },
    { code: 'DE', name: labels?.germany || 'Germany', dialCode: '+49', flag: '🇩🇪' },
    { code: 'ES', name: labels?.spain || 'Spain', dialCode: '+34', flag: '🇪🇸' },
    { code: 'IT', name: labels?.italy || 'Italy', dialCode: '+39', flag: '🇮🇹' },
  ];
}

export interface StonePhoneInputProps {
  /**
   * Current value (full phone number with country code)
   */
  value?: string;
  /**
   * Callback when value changes
   */
  onChange: (value: string) => void;
  /**
   * Input placeholder
   */
  placeholder?: string;
  /**
   * Default country code
   */
  defaultCountry?: string;
  /**
   * Available countries
   */
  countries?: CountryCode[];
  /**
   * Input label
   */
  label?: string;
  /**
   * Whether input is required
   */
  required?: boolean;
  /**
   * Error message
   */
  error?: string;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Translation messages for country names
   */
  countryLabels?: Record<string, string>;
}

/**
 * StonePhoneInput - International phone input
 */
export function StonePhoneInput({
  value = '',
  onChange,
  placeholder = '6 00 00 00 00',
  defaultCountry = 'FR',
  countries,
  label,
  required = false,
  error,
  className = '',
  countryLabels,
}: StonePhoneInputProps) {
  const resolvedCountries = countries || getDefaultCountries(countryLabels);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(
    resolvedCountries.find(c => c.code === defaultCountry) || resolvedCountries[0]!
  );
  const [phoneNumber, setPhoneNumber] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Parse initial value
  useEffect(() => {
    if (value) {
      // Try to detect country from value
      const matchedCountry = resolvedCountries.find(c => value.startsWith(c.dialCode));
      if (matchedCountry) {
        setSelectedCountry(matchedCountry);
        setPhoneNumber(value.slice(matchedCountry.dialCode.length).trim());
      } else {
        setPhoneNumber(value);
      }
    }
  }, []);
  
  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleCountrySelect = (country: CountryCode) => {
    setSelectedCountry(country);
    setIsOpen(false);
    onChange(`${country.dialCode} ${phoneNumber}`);
  };
  
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = e.target.value;
    setPhoneNumber(newNumber);
    onChange(`${selectedCountry.dialCode} ${newNumber}`);
  };
  
  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 font-sans mb-2">
          {label} {required && '*'}
        </label>
      )}
      
      <div className={`flex border ${error ? 'border-red-400' : 'border-stone-300'} focus-within:border-stone-500 transition-colors`}>
        {/* Country Selector */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-4 py-3 border-r border-stone-300 bg-stone-50 hover:bg-stone-100 transition-colors"
        >
          <span className="text-lg">{selectedCountry.flag}</span>
          <span className="text-sm font-sans text-stone-700">{selectedCountry.dialCode}</span>
          <ChevronDown 
            size={14} 
            className={`text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </button>
        
        {/* Phone Input */}
        <div className="flex-1 relative">
          <input
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder={placeholder}
            required={required}
            className="w-full h-full px-4 py-3 bg-white font-sans text-stone-900 placeholder:text-stone-400 focus:outline-none"
          />
        </div>
      </div>
      
      {/* Error */}
      {error && (
        <p className="text-red-500 text-xs font-sans mt-1">{error}</p>
      )}
      
      {/* Country Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 max-h-60 overflow-y-auto bg-white border border-stone-200 shadow-lg z-50">
          {resolvedCountries.map((country) => (
            <button
              key={country.code}
              type="button"
              onClick={() => handleCountrySelect(country)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-stone-50 transition-colors ${
                country.code === selectedCountry.code ? 'bg-stone-50' : ''
              }`}
            >
              <span className="text-lg">{country.flag}</span>
              <span className="flex-1 text-sm font-sans text-stone-700">{country.name}</span>
              <span className="text-sm font-sans text-stone-500">{country.dialCode}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default StonePhoneInput;
